/**
 * Settings Service
 *
 * Service for managing user settings in Firestore
 * Handles CRUD operations for all settings categories
 */

import { getFirestoreClient } from "../lib/firestoreClient";
import type { Result } from "../types/result";
import { Ok, Err } from "../types/result";
import type { AppError } from "../types/errors";
import { createUnexpectedError } from "../types/errors";
import { createLogger } from "../utils/logger";
import type { SettingsCategory, SettingsData } from "../types/settings";
import { isSettingsCategory } from "../types/settings";

/**
 * All settings categories in canonical order.
 */
const ALL_CATEGORIES: readonly SettingsCategory[] = [
  "profile",
  "investableAssets",
  "realEstateAssets",
  "liabilities",
  "income",
  "expenses",
  "assumptions",
] as const;

/** Map of category → parsed settings data (or null if missing). */
export type AllSettingsMap = {
  [K in SettingsCategory]: SettingsData | null;
};

const settingsServiceLogger = createLogger({ prefix: "SettingsService" });

/**
 * Get settings data for a specific category
 *
 * @param userId - User ID
 * @param category - Settings category (profile, assets, income, expenses, assumptions)
 * @returns Result containing the settings data or null if not found
 */
export async function getSettings<T extends SettingsData = SettingsData>(
  userId: string,
  category: SettingsCategory
): Promise<Result<T | null, AppError>> {
  if (!userId) {
    settingsServiceLogger.error(
      "Precondition failed: userId is required for getSettings",
      {
        userId,
        category,
      }
    );
    return Err(
      createUnexpectedError({
        error: new Error("Missing userId"),
        context: `getSettings called without userId for category=${category}`,
        cause: { userId, category },
      })
    );
  }

  settingsServiceLogger.debug("getSettings called", {
    userId,
    category,
    timestamp: new Date().toISOString(),
  });

  settingsServiceLogger.debug("Fetching settings from Firestore", {
    userId,
    category,
  });

  const client = getFirestoreClient();
  const path = ["users", userId, "settings", category];

  const settingsDoc = await client.getDocument(path);

  if (!settingsDoc.exists()) {
    settingsServiceLogger.debug("Settings not found in Firestore", {
      userId,
      category,
    });
    return Ok(null);
  }

  const rawData = settingsDoc.data();
  const parsedData = parseSettingsData(rawData ?? {}, category);

  return Ok(parsedData as T);
}

/**
 * Get all settings for a user in a single Firestore collection query.
 *
 * Instead of 7 separate `getDoc` calls (one per category), this fetches the
 * entire `users/{userId}/settings` collection in one round-trip, reducing
 * Firestore read overhead from 7 calls to 1.
 *
 * @param userId - User ID
 * @returns Result containing a map of category → data (or null per category)
 */
export async function getAllSettings(
  userId: string
): Promise<Result<AllSettingsMap, AppError>> {
  if (!userId) {
    settingsServiceLogger.error(
      "Precondition failed: userId is required for getAllSettings",
      { userId }
    );
    return Err(
      createUnexpectedError({
        error: new Error("Missing userId"),
        context: "getAllSettings called without userId",
        cause: { userId },
      })
    );
  }

  settingsServiceLogger.debug("Fetching all settings in single query", {
    userId,
  });

  const client = getFirestoreClient();
  const collectionPath = ["users", userId, "settings"];

  const queryResult = await client.queryCollection(collectionPath);

  // Build the result map, initializing every category to null
  const result: AllSettingsMap = {} as AllSettingsMap;
  for (const cat of ALL_CATEGORIES) {
    result[cat] = null;
  }

  // Populate from query results — doc IDs are the category names
  for (const doc of queryResult.docs) {
    const docId = doc.id;
    if (isSettingsCategory(docId)) {
      const rawData = doc.data();
      if (rawData) {
        result[docId] = parseSettingsData(rawData, docId);
      }
    }
  }

  return Ok(result);
}

/**
 * Update settings data for a specific category.
 * Writes are awaited to ensure acknowledgment.
 *
 * @param userId - User ID
 * @param category - Settings category
 * @param data - Partial settings data to update
 * @returns Result indicating success or error
 */
export async function updateSettings(
  userId: string,
  category: SettingsCategory,
  data: Partial<SettingsData>
): Promise<Result<void, AppError>> {
  settingsServiceLogger.debug("Updating settings", {
    userId,
    category,
    fields: Object.keys(data),
  });

  if (!userId) {
    settingsServiceLogger.error(
      "Precondition failed: userId is required for updateSettings",
      {
        userId,
        category,
        fields: Object.keys(data),
      }
    );
    return Err(
      createUnexpectedError({
        error: new Error("Missing userId"),
        context: `updateSettings called without userId for category=${category}`,
        cause: { userId, category, fields: Object.keys(data) },
      })
    );
  }

  const client = getFirestoreClient();
  const path = ["users", userId, "settings", category];
  const firestoreData = convertDatesToTimestamps(data);

  await client.setDocument(
    path,
    { ...firestoreData, updatedAt: client.serverTimestamp() },
    { merge: true }
  );

  settingsServiceLogger.info("Settings saved", { userId, category });
  return Ok(undefined);
}

/**
 * Subscribe to real-time settings updates for a specific category.
 * Uses Firestore onSnapshot for automatic updates when data changes.
 *
 * This is the recommended way to load settings in React components.
 *
 * @param userId - User ID
 * @param category - Settings category
 * @param callback - Called with the latest data whenever it changes
 * @returns Unsubscribe function to stop listening
 */
export function subscribeToSettings<T extends SettingsData = SettingsData>(
  userId: string,
  category: SettingsCategory,
  callback: (data: T | null) => void
): () => void {
  if (!userId) {
    settingsServiceLogger.error(
      "Precondition failed: userId is required for subscribeToSettings",
      { userId, category }
    );
    return () => {};
  }

  const client = getFirestoreClient();
  const path = ["users", userId, "settings", category];

  settingsServiceLogger.debug("Subscribing to settings", {
    userId,
    category,
  });

  return client.onDocumentSnapshot(
    path,
    (snapshot) => {
      if (snapshot.exists()) {
        const data = parseSettingsData(
          snapshot.data() ?? {},
          category
        ) as unknown as T;
        settingsServiceLogger.debug("Settings snapshot received", {
          userId,
          category,
          exists: true,
        });
        callback(data);
      } else {
        settingsServiceLogger.debug("Settings snapshot received (no data)", {
          userId,
          category,
          exists: false,
        });
        callback(null);
      }
    },
    (error) => {
      settingsServiceLogger.error("Settings snapshot error", {
        userId,
        category,
        error,
      });
    }
  );
}

/**
 * Save a settings update immediately.
 *
 * @param userId - User ID
 * @param category - Settings category
 * @param data - Partial settings data to save
 */
export async function saveSettings(
  userId: string,
  category: SettingsCategory,
  data: Partial<SettingsData>
): Promise<void> {
  if (!userId || Object.keys(data).length === 0) return;

  const client = getFirestoreClient();
  const path = ["users", userId, "settings", category];
  const converted = convertDatesToTimestamps(data);

  await client.setDocument(
    path,
    { ...converted, updatedAt: client.serverTimestamp() },
    { merge: true }
  );
}

/**
 * Parse settings data from Firestore format (Timestamps) to application format (Dates)
 */
function parseSettingsData(
  data: Record<string, unknown>,
  category: SettingsCategory
): SettingsData {
  const parsed: Record<string, unknown> = { ...data };

  // Remove metadata fields
  delete parsed.createdAt;
  delete parsed.updatedAt;

  // Convert Timestamps to Dates for profile birthdate
  if (category === "profile") {
    if (
      parsed.birthdate &&
      typeof parsed.birthdate === "object" &&
      "toDate" in parsed.birthdate
    ) {
      parsed.birthdate = (parsed.birthdate as { toDate(): Date }).toDate();
    }
  }

  // Cast as SettingsData after parsing
  return parsed as unknown as SettingsData;
}

/**
 * Convert Date objects to Firestore Timestamps for storage
 */
function convertDatesToTimestamps(
  data: Partial<SettingsData>
): Record<string, unknown> {
  const converted: Record<string, unknown> = { ...data };

  // Convert Date objects to Firestore-compatible timestamps
  const client = getFirestoreClient();
  Object.keys(converted).forEach((key) => {
    const value = converted[key];
    if (value instanceof Date) {
      converted[key] = client.timestampFromDate(value);
    }
  });

  return converted;
}
